import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, BookOpen, ChevronDown, UserCircle, ExternalLink } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import GTranslate from './GTranslate';
import styles from './Navbar.module.css';

interface NavItem {
  label: string;
  id: string;
  path: string;
  isPrimary?: boolean;
  isHighlight?: boolean;
  subItems?: { label: string; id: string; path: string; hash?: string }[];
}

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems: NavItem[] = [
    { label: 'Beranda', id: 'home', path: '/' },
    {
      label: 'Tentang Kami',
      id: 'about',
      path: '/tentang',
      subItems: [
        { label: 'Sejarah', id: 'history', path: '/sejarah' },
        { label: 'Visi, Misi & Nilai', id: 'visimisi', path: '/tentang', hash: '#visimisi' },
        { label: 'Susunan Pengurus', id: 'structure', path: '/pengurus' },
        { label: 'Staf & Manajemen', id: 'staff', path: '/staf' },
        { label: 'Jaringan', id: 'network', path: '/tentang', hash: '#network' }
      ]
    },
    { label: 'Riset & Publikasi', id: 'repository', path: '/riset', isPrimary: true },
    {
      label: 'Program',
      id: 'program',
      path: '#',
      subItems: [
        { label: 'Program Inklusi', id: 'program-inklusi', path: '/program-inklusi' },
        { label: 'Program P2MI BK', id: 'p2mi-bk', path: '/p2mi-bk' },
        { label: 'SIM 3PD', id: 'https://lakpesdam.org/login', path: 'https://lakpesdam.org/login' },
      ]
    },
    { label: 'Berita', id: 'news', path: '/berita' },
    { label: 'Beasiswa NU', id: 'https://nuscholarship.or.id/', path: 'https://nuscholarship.or.id/', isHighlight: true },
    { label: 'Taswirul Afkar', id: 'https://tashwirulafkar.or.id/index.php/afkar', path: 'https://tashwirulafkar.or.id/index.php/afkar', isHighlight: true },
    { label: 'Bappenu', id: 'https://bappenu.or.id/index.php', path: 'https://bappenu.or.id/index.php', isHighlight: true },
    { label: 'SidakNU', id: 'https://sidaknu.lakpesdam.org/login', path: 'https://sidaknu.lakpesdam.org/login', isHighlight: true },
  ];

  const handleMouseEnter = (itemId: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(itemId);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };

  const handleNavClick = (item: NavItem) => {
    if (item.path.startsWith('http')) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
      setIsMobileMenuOpen(false);
      return;
    }

    if (!item.subItems) {
      navigate(item.path);
      setIsMobileMenuOpen(false);
    } else if (item.id === 'about') {
      navigate('/tentang');
      setIsMobileMenuOpen(false);
    }
  };

  const handleSubItemClick = (sub: NonNullable<NavItem['subItems']>[0]) => {
    if (sub.path.startsWith('http')) {
      window.open(sub.path, '_blank', 'noopener,noreferrer');
      setIsMobileMenuOpen(false);
      setActiveDropdown(null);
      return;
    }

    if (sub.hash) {
      navigate(`${sub.path}${sub.hash}`);
      
      // Attempt to scroll if we are already on the page
      setTimeout(() => {
        const element = document.getElementById(sub.hash!.substring(1));
        if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    } else {
      navigate(sub.path);
    }

    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const isPathActive = (path: string, itemHasHash?: boolean) => {
    if (path === '/') return location.pathname === '/';
    if (itemHasHash) return false;
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <div className={styles.Navbar_superNav}>
        <div className={`container ${styles.Navbar_superNav_container}`}>
          <div className={styles.Navbar_superNav_left}>
            {navItems.filter(item => item.isHighlight).map((item) => (
              <a key={item.id} href={item.path} target="_blank" rel="noopener noreferrer" className={styles.Navbar_superNav_link}>
                {item.label} <ExternalLink size={10} style={{ marginLeft: 2, opacity: 0.7 }} />
              </a>
            ))}
          </div>
          <div className={styles.Navbar_superNav_right}>
            <GTranslate isScrolled={false} />
          </div>
        </div>
      </div>
      <nav className={`${styles.Navbar_nav} ${isScrolled ? styles.Navbar_navScrolled : styles.Navbar_navTransparent}`}>
        <div className={`${styles.Navbar_wrapper} container`}>
          <Link 
            to="/"
            className={styles.Navbar_logoContainer} 
            role="button"
            tabIndex={0}
            aria-label="Kembali ke Beranda"
            style={{ textDecoration: 'none' }}
          >
            {!logoError ? (
              <img
                src="/logo.png"
                alt="Logo Lakpesdam"
                className={styles.Navbar_logoImage}
                width="44"
                height="44"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className={styles.Navbar_logoIcon}>L</div>
            )}
            <div className={styles.Navbar_logoText}>
              Lakpesdam<span className={styles.Navbar_logoSub}>PBNU</span>
            </div>
          </Link>

          <div className={styles.Navbar_menuDesktop}>
            {navItems.filter(item => !item.isHighlight).map((item) => (
              <div
                key={item.id}
                className={styles.Navbar_navItemWrapper}
                onMouseEnter={() => item.subItems && handleMouseEnter(item.id)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  onClick={() => handleNavClick(item)}
                  className={
                    item.isPrimary
                      ? styles.Navbar_navLinkPrimary
                      : item.isHighlight
                        ? styles.Navbar_navLinkHighlight
                        : `${styles.Navbar_navLink} ${isPathActive(item.path) ? styles.Navbar_navLinkActive : ''}`
                  }
                >
                  {item.isPrimary && <BookOpen size={16} />}
                  {item.label}
                  {item.path.startsWith('http') && <ExternalLink size={12} style={{ marginLeft: 4, opacity: 0.7 }} />}
                  {item.subItems && <ChevronDown size={14} style={{ marginLeft: 4 }} />}
                </button>

                {item.subItems && activeDropdown === item.id && (
                  <div className={styles.Navbar_dropdown}>
                    {item.subItems.map(sub => (
                      <button
                        key={sub.id}
                        className={styles.Navbar_dropdownItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSubItemClick(sub);
                        }}
                      >
                        {sub.label}
                        {sub.path.startsWith('http') && <ExternalLink size={10} style={{ marginLeft: 4, opacity: 0.5 }} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <Link
              to="/kontributor"
              className={`${styles.Navbar_navLink} ${isPathActive('/kontributor') ? styles.Navbar_navLinkActive : ''}`}
              title="Akses Kontributor"
              aria-label="Login Kontributor"
            >
              <UserCircle size={22} />
            </Link>
          </div>

          <div className={styles.Navbar_mobileToggle}>
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Tutup menu" : "Buka menu"}
              aria-expanded={isMobileMenuOpen}
              style={{ color: 'white' }}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className={styles.Navbar_mobileMenu}>
            <div className="p-4" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.4)' }}>
              <GTranslate isScrolled={false} />
            </div>

            {navItems.map((item) => (
              <div key={item.id} className={styles.Navbar_mobileItemGroup}>
                <button
                  onClick={() => handleNavClick(item)}
                  className={`${styles.Navbar_mobileMenuItem} ${item.isPrimary ? styles.Navbar_mobileMenuItemPrimary : ''} ${item.isHighlight ? styles.Navbar_mobileMenuItemHighlight : ''}`}
                >
                  {item.label}
                  {item.path.startsWith('http') && <ExternalLink size={14} className="inline ml-2 opacity-70" />}
                </button>
                {item.subItems && (
                  <div className={styles.Navbar_mobileSubMenu}>
                    {item.subItems.map(sub => (
                      <button
                        key={sub.id}
                        className={styles.Navbar_mobileSubItem}
                        onClick={() => handleSubItemClick(sub)}
                      >
                        — {sub.label}
                        {sub.path.startsWith('http') && <ExternalLink size={12} className="inline ml-1 opacity-50" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/kontributor"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`${styles.Navbar_mobileMenuItem} text-emerald-200`}
              style={{ textDecoration: 'none' }}
            >
              <UserCircle size={18} className="inline mr-2" /> Login Kontributor
            </Link>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
